import {
  CHAT_HISTORY_STORAGE_KEY_PREFIX,
  DEFAULT_CHAT_TOPIC,
} from '../constants/ChatModal.constants';
import {CHAT_TEXT} from '../constants/chatText.constants';
import {ChatTopic, ConversationThread} from '../types/ChatModal.types';

export const createThreadId = () =>
  `thread-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createMessageId = () =>
  `message-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const isChatTopic = (value: unknown): value is ChatTopic =>
  value === 'orders' ||
  value === 'rank' ||
  value === 'commissions' ||
  value === 'bonuses';

export const createEmptyConversation = (
  topic: ChatTopic = DEFAULT_CHAT_TOPIC,
): ConversationThread => ({
  id: createThreadId(),
  title: CHAT_TEXT.newChat,
  topic,
  messages: [],
  suggestions: [],
  hasUsedSuggestion: false,
  conversationId: null,
  externalSessionId: null,
  updatedAt: new Date().toISOString(),
});

export const isDraftConversation = (
  conversation: ConversationThread,
): boolean =>
  conversation.messages.length === 0 &&
  !conversation.conversationId &&
  !conversation.externalSessionId;

export const getDeploymentConversationIdCandidates = (
  conversation: ConversationThread | null | undefined,
): string[] => {
  if (!conversation) {
    return [];
  }

  const candidates = [
    conversation.conversationId,
    conversation.externalSessionId,
  ]
    .map(value => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean);

  return Array.from(new Set(candidates));
};

export const createChatHistoryStorageKey = (userId: string | number): string =>
  `${CHAT_HISTORY_STORAGE_KEY_PREFIX}-${userId}`;

export const buildConversationTitle = (text: string): string => {
  const normalized = text.trim().replace(/\s+/g, ' ');
  if (!normalized) {
    return CHAT_TEXT.newChat;
  }

  return normalized.length > 28 ? `${normalized.slice(0, 28)}...` : normalized;
};

export const buildSupportReference = (
  conversation: ConversationThread,
): string =>
  conversation.conversationId || conversation.externalSessionId || '';

export const formatSupportReferenceLabel = (
  conversation: ConversationThread,
  ts: (key: string, fallback?: string) => string,
): string => {
  const supportReference = buildSupportReference(conversation);

  if (!supportReference) {
    return '';
  }

  return `${ts(
    'chat.supportReferenceLabel',
    CHAT_TEXT.supportReferenceLabelPrefix,
  )}: ${supportReference}`;
};

export const getContextualLoadingMessages = (prompt: string): string[] => {
  const lower = prompt.toLowerCase();

  if (/order|cv|qv|client order|purchase/.test(lower)) {
    return [
      'Looking up order data for you...',
      'Reviewing order history and totals...',
      'Compiling your order insights...',
      'Still working on this. Thanks for your patience.',
    ];
  }

  if (/commission|payout|earning|paid/.test(lower)) {
    return [
      'Fetching your commission records...',
      'Calculating payouts and trends...',
      'Putting together your commission summary...',
      'Still working on this. Thanks for your patience.',
    ];
  }

  if (/rank|director|title|promotion|advance/.test(lower)) {
    return [
      'Checking your current rank status...',
      'Reviewing rank qualifications and progress...',
      'Summarising your rank insights...',
      'Still working on this. Thanks for your patience.',
    ];
  }

  if (/bonus|incentive|reward/.test(lower)) {
    return [
      'Pulling your bonus and incentive data...',
      'Reviewing eligibility and payouts...',
      'Crafting your bonus summary...',
      'Still working on this. Thanks for your patience.',
    ];
  }

  if (/team|downline|coach|organisation|organization/.test(lower)) {
    return [
      'Gathering your team data...',
      'Reviewing downline performance...',
      'Building your team insights...',
      'Still working on this. Thanks for your patience.',
    ];
  }

  if (/client|customer|member/.test(lower)) {
    return [
      'Looking up your client records...',
      'Reviewing client activity and history...',
      'Putting together your client insights...',
      'Still working on this. Thanks for your patience.',
    ];
  }

  return [
    'Pulling together the right data for you...',
    'Reviewing and comparing relevant records...',
    'Crafting a clear response with latest insights...',
    'Still working on this. Thanks for your patience.',
  ];
};
